import {
  ListChecks,
  History,
  Settings,
  Cpu,
  ShieldCheck,
  BookOpen,
  Building2,
  FileText,
  SlidersHorizontal,
  ClipboardList,
  Layers,
  Activity,
  Megaphone,
  KeyRound,
  Receipt,
  CalendarClock,
  FileStack,
  LayoutDashboard,
  Gauge,
  Tags,
  Scale,
  type LucideIcon,
} from 'lucide-react'
import type { AccessGates, ModuleKey } from '@/shared/rbac'

export interface NavItem {
  label: string
  icon: LucideIcon
  path: string
  /**
   * Post-refactor: the module slug that gates visibility of this nav item.
   * The sidebar shows the item iff `useAccess().can(module, 'read')` is true.
   * Preferred over the legacy `access` gate string.
   */
  module?: ModuleKey
  /**
   * Legacy: named access gate from useAccess(). Still supported for items
   * whose visibility depends on a composite condition (multi-module OR).
   * If both `module` and `access` are set, `module` wins.
   */
  access?: keyof AccessGates
  /**
   * When true, the item is only shown to superadmins (system_admin:edit:all),
   * on top of any `module`/`access` gate. Non-superadmins never see it.
   */
  superadminOnly?: boolean
  /** Requires `edit:all` on the item's module (e.g. biller super admin), not system admin. */
  editAllOnly?: boolean
  /**
   * A SECOND module the user must also be able to read. Use when one
   * destination belongs to different sections for different audiences — the
   * same item can then appear under two group labels without either audience
   * seeing it twice (2026-08-06: Timesheet under Billing vs My Time).
   */
  alsoRequires?: ModuleKey
  /** Hidden from anyone who CAN read this module — the mirror of the above. */
  hiddenWhen?: ModuleKey
  /**
   * When true, the sidebar shows a small unread dot on this item while the user
   * has unread announcements (drives off the updates unread-count query).
   */
  unreadDot?: boolean
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

/**
 * Section ordering — top to bottom:
 *   1. Workspace          — daily coding work + manager notes
 *   2. Dashboards         — role-specific admin landings (Client / Engine)
 *   3. Knowledge Base     — reference data the platform exposes
 *   4. Clients            — cross-client directory (Super Admin lookup)
 *   5. System             — platform administration (audit logs live under /system-admin/audit-logs)
 *
 * Note: Notifications page (`/notifications`) exists but is intentionally not in the
 * sidebar. Entry point is the bell icon in the top header → "View all notifications".
 *
 * Access gates match the route guards in app/router.tsx so an item never
 * appears in the sidebar unless the user can actually load the page behind it.
 */
export const navigation: NavGroup[] = [
  {
    label: 'Workspace',
    items: [
      { label: 'Chart Queue', icon: ListChecks, path: '/workspace/queue', module: 'chart_queue' },
      { label: 'History', icon: History, path: '/workspace/history', module: 'history' },
      { label: 'Notes', icon: FileText, path: '/workspace/notes', module: 'notes' },
    ],
  },
  {
    label: 'Dashboards',
    items: [
      { label: 'Charts Dashboard', icon: Settings, path: '/client-admin/overview', module: 'charts_dashboard' },
      { label: 'Performance Dashboard', icon: Gauge, path: '/performance-dashboard', module: 'charts_dashboard' },
      { label: 'Operations Dashboard', icon: Activity, path: '/operations-dashboard', module: 'operations_dashboard' },
      { label: 'Analytics Dashboard', icon: Cpu, path: '/engine-admin/overview', module: 'analytics_dashboard' },
      { label: 'Billing Impact', icon: Scale, path: '/billing-impact/summary', module: 'billing_impact' },
    ],
  },
  {
    label: 'Master Library',
    items: [
      { label: 'Knowledge Base', icon: BookOpen, path: '/kb', module: 'knowledge_base' },
      { label: 'Mandatory Fields', icon: ClipboardList, path: '/platform-config/mandatory-fields', module: 'mandatory_fields' },
      { label: 'Prompt Library', icon: SlidersHorizontal, path: '/prompt-config', module: 'prompt_config' },
      { label: 'Configurations', icon: Layers, path: '/client-config', module: 'client_config' },
    ],
  },
  {
    label: 'Clients',
    items: [
      { label: 'Clients', icon: Building2, path: '/clients', module: 'clients' },
    ],
  },
  {
    /**
     * A coder's own hours — NOT under Billing (Hitesh, 2026-08-06). Coders hold
     * `timesheet` and nothing else in this area, and filing your own time is
     * not billing work; seeing it under a billing header implied a surface they
     * have no access to. Billing users get the same destination inside the
     * Finance Overview group below, so nobody sees Timesheet twice.
     */
    label: 'My Time',
    items: [
      { label: 'Timesheet', icon: CalendarClock, path: '/timesheet', module: 'timesheet', hiddenWhen: 'invoicing' },
    ],
  },
  {
    // "Overview", not "Billing" (2026-08-22). Once per-client work moved onto
    // the client record, everything left in this group became a CROSS-CLIENT
    // roll-up: hours across every coder, invoices across every client, pricing
    // across the book. The group name now describes the scope its screens
    // share, and "Clients › <client>" is where the same work happens for one.
    //
    // Qualified to "Finance Overview" (2026-09-23): bare "Overview" collided
    // with the Overview tab inside Charts Dashboard (/client-admin/overview)
    // and Analytics Dashboard (/engine-admin/overview), so a user reading the
    // sidebar had no way to tell which surface it meant. The scope rationale
    // above is unchanged — only the noun it is attached to.
    label: 'Finance Overview',
    items: [
      // Hours imports are reached from inside an invoice draft (Import hours),
      // not as a standalone destination, so they get no sidebar item.
      //
      // Contracts & Pricing is the cross-client OVERVIEW (2026-08-22): every
      // client's rates and contract status on one screen, read-only, with an
      // Edit link into that client's own tab. Editing a rate card needs the
      // facility and care-setting context that only the client record has.
      //
      // Timesheet STAYED, alongside the per-client tab, because the two answer
      // different questions. Checking hours is a cross-client sweep — "is
      // anyone's time missing or wrong" spans every coder, because absences and
      // typos do not respect client boundaries. Billing those hours is
      // per-client, because an invoice is. Same page, two scopes, exactly as
      // Invoices already works. Coders reach their own under My Time, where
      // `hiddenWhen` keeps the two from both showing.
      // Finance dashboard (2026-08-18): one client's billing at a glance —
      // rate cards, invoices, contracts, and the issued-to-paid timeline.
      { label: 'Dashboard', icon: LayoutDashboard, path: '/invoicing/dashboard', module: 'invoicing' },
      { label: 'Timesheet', icon: CalendarClock, path: '/timesheet', module: 'timesheet', alsoRequires: 'invoicing' },
      { label: 'Invoices', icon: Receipt, path: '/invoicing/invoices', module: 'invoicing' },
      // NOTE: there is no separate "Monthly billing" item. Invoices itself
      // renders the month-accrual view when the entity switch is on Medicodio
      // and the ledger otherwise — the two books bill differently, so the
      // switch changes the page's shape (see features/invoicing/routes.tsx).
      { label: 'Contracts & Pricing', icon: FileStack, path: '/invoicing/contracts-pricing', module: 'invoicing' },
      // Import workflows has NO sidebar item (Karthik, 2026-10-27). It was
      // added 2026-08-04 reversing an earlier removal, then narrowed to super
      // admin on 2026-08-06; this removes the destination again, as not
      // required.
      //
      // The PAGE and its route are deliberately left in place: recipes are
      // still created and edited there, the import wizard's own copy still
      // sends people to it, and it is still reachable at
      // /invoicing/import-workflows. Only the nav entry is gone — deleting a
      // working page is a larger call than tidying the sidebar.
      // Master service catalog (2026-08-27): standard prices client cards
      // discount from. Platform-wide, so super admin only like workflows.
      { label: 'Service catalog', icon: Tags, path: '/invoicing/catalog', module: 'invoicing', editAllOnly: true },
    ],
  },
  {
    label: 'Access',
    items: [
      { label: 'Access Requests', icon: KeyRound, path: '/access', module: 'access_request' },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'System Admin', icon: ShieldCheck, path: '/system-admin/clients', module: 'system_admin' },
      // Announcements — gated by the composite `canViewAnnouncements` gate
      // (updates read + the announcements_module feature flag), so it stays hidden
      // while the kill-switch is off. Sits below System Admin per latest UX.
      // The internal module key stays `updates`; only the display label changed.
      { label: 'Announcements', icon: Megaphone, path: '/announcements', access: 'canViewAnnouncements', unreadDot: true },
    ],
  },
]
